<template>
  <div class="search">
    <div v-if="error" class="search__error">
      <fa-icon :icon="['fas', 'sad-tear']" />
    </div>
    <input
      v-model="searchText"
      :class="['search__field', className, { 'search__field--error': error }]"
      type="search"
      name="search"
      :placeholder="error ? errorText : placeholder"
      @keyup="clearError"
      @keyup.enter="triggerSearch"
    />
    <button @click="triggerSearch">
      <fa-icon :icon="['fas', 'search']" />
    </button>
  </div>
</template>

<script setup lang="ts">
  defineProps<{
    placeholder?: string;
    className?: string;
  }>();

  const route = useRoute();
  const router = useRouter();
  const searchStore = useSearchStore();
  const { trackSearch } = useAnalytics();

  const searchText = ref("");
  const error = ref(false);
  const errorText = "Enter some words to search...";
  let timer: NodeJS.Timeout | null = null;

  watch(
    () => route.fullPath,
    () => {
      if (route.path !== "/search") {
        searchText.value = "";
        searchStore.searchRequest("");
        error.value = false;
      }
    }
  );

  function triggerSearch() {
    if (searchText.value.trim()) {
      clearError();
      const search = { text: searchText.value };
      if (searchStore.getSearchText === search.text) return;
      searchStore.searchRequest(search.text);

      if (searchText.value.trim()) {
        router.push("/search");
        trackSearch(searchText.value);
      }
    } else {
      error.value = true;
    }
  }

  function clearError() {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }

    timer = setTimeout(() => {
      error.value = !searchText.value.trim();
    }, 400);
  }
</script>

<style lang="scss" scoped>
  .search {
    width: 40%;
    display: flex;
    position: relative;

    button {
      position: absolute;
      top: 51%;
      right: 20px;
      transform: translateY(-50%);
      animation: 0.3s appear;
      outline: none;

      svg {
        width: 40px;
        height: 30px;
      }

      &:hover {
        color: $pforange;
      }
    }
  }

  .btn {
    margin-left: -10px;
  }

  .search__field {
    border: 2px solid $pfcream;
    width: 100%;
    padding: 10px 20px 10px 10px;
    border-radius: 0;
    outline: none;

    &::-webkit-search-cancel-button {
      appearance: none;
      display: none;
    }

    &:focus {
      box-shadow: 0 0 3px 2px $pfgreen2;
    }
  }

  .search__field--error {
    border-color: $error;

    &:focus {
      box-shadow: 0 0 3px 2px $error;
    }
  }

  .search__field--banner {
    border-color: rgba($pfgreen, 0.6);
  }

  .search__error {
    position: absolute;
    top: 50%;
    left: -40px;
    transform: translateY(-50%);
    animation: 0.3s appear;

    svg {
      width: 30px;
      height: 30px;
      color: $error;
    }
  }
</style>
